import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { useLiveHosts } from '../rooms/useLiveHosts';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import type { TrackSummary } from '../tracks/TrackList';
import { FAVORITES_KEY, favorites, playlists, type Playlist } from './api';
import { PlaylistTracks } from './PlaylistTracks';

/**
 * The library, docked at the left-hand end of the player bar.
 *
 * ## Why it is in the bar rather than a page
 *
 * Same argument as the chat rail at the other end: this is quick access to
 * things you want *while* doing something else. Going to a page to find a
 * favourite means leaving whatever you were reading, and the three lists here -
 * what you saved, who is on air, what you collected - are all things you reach
 * for mid-browse and then carry on.
 *
 * The pages still exist and are still the better place to sit and work. This is
 * the drawer, not the filing cabinet.
 *
 * ## Desktop only
 *
 * Matching the chat rail, and for the same reason: a 320px panel docked inside
 * a 360px viewport is a full-screen sheet wearing a costume, and the routes are
 * already the better version of that on a phone.
 */

const WIDTH = 320;
const HEIGHT = 400;

type Tab = 'favorites' | 'hosts' | 'playlists';

const TABS: { id: Tab; label: string }[] = [
  { id: 'favorites', label: 'Favorites' },
  { id: 'hosts', label: 'Hosts' },
  { id: 'playlists', label: 'Playlists' },
];

export function LibraryDock() {
  const user = useSession((state) => state.user);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('favorites');

  /*
   * Which playlist is drilled into, if any.
   *
   * Kept here rather than inside the playlists tab so that closing the panel
   * and reopening it returns to the list. A panel that reopens three levels
   * deep, where you left it a navigation ago, is one you have to back out of
   * before you can use it.
   */
  const [inside, setInside] = useState<number | null>(null);

  const root = useRef<HTMLDivElement | null>(null);

  /* Escape, and a click anywhere else. See `ChatLauncher` for the pointerdown. */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);

    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  if (!user) return null;

  const show = (next: Tab) => {
    setTab(next);
    setInside(null);
  };

  return (
    <div
      className="relative hidden h-full md:flex md:items-stretch"
      data-dock-library
      ref={root}
    >
      {open && (
        <div
          style={{ width: WIDTH, height: HEIGHT }}
          data-library-panel
          className="absolute bottom-full left-0 z-10 flex flex-col overflow-hidden rounded-t-[var(--radius-panel)] border border-b-0 border-line-lit bg-raised shadow-2xl"
        >
          {inside !== null ? (
            <PlaylistTracks
              playlistId={inside}
              onBack={() => setInside(null)}
              onClose={() => setOpen(false)}
            />
          ) : (
            <>
              <div className="flex shrink-0 border-b border-line" role="tablist">
                {TABS.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === entry.id}
                    data-library-tab={entry.id}
                    onClick={() => show(entry.id)}
                    className={`flex-1 px-2 py-2.5 text-[13px] font-medium transition-colors ${
                      tab === entry.id
                        ? 'border-b-2 border-accent text-ink'
                        : 'border-b-2 border-transparent text-soft hover:text-ink'
                    }`}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                {tab === 'favorites' && <FavoritesTab onPicked={() => setOpen(false)} />}
                {tab === 'hosts' && <HostsTab onPicked={() => setOpen(false)} />}
                {tab === 'playlists' && (
                  <PlaylistsTab username={user.username} onOpen={setInside} />
                )}
              </div>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        data-library-trigger
        className={`flex h-full items-center gap-2 border-r border-line px-5 transition-colors ${
          open ? 'bg-raised text-ink' : 'text-soft hover:bg-raised hover:text-ink'
        }`}
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
          <path d="M2 2h3v12H2zM6 2h3v12H6zM10.5 2.6l2.9 11.2-1.9.5L8.6 3.1z" />
        </svg>

        <span className="text-sm font-medium">Library</span>
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Favourites                                                                 */
/* -------------------------------------------------------------------------- */

function FavoritesTab({ onPicked }: { onPicked: () => void }) {
  const play = usePlayer((state) => state.play);

  const { data, isPending } = useQuery({ queryKey: FAVORITES_KEY, queryFn: favorites });

  const items = data?.items ?? [];

  if (isPending) return <Loading />;

  if (items.length === 0) {
    return (
      <Empty>
        Nothing saved yet. The heart on any track — including the one in the
        player bar — puts it here.
      </Empty>
    );
  }

  return (
    <>
      {items.map((track) => (
        <button
          key={track.id}
          type="button"
          data-library-track={track.id}
          onClick={() => {
            /*
             * The rest of the list goes with it.
             *
             * Playing one favourite and then falling silent is not what a list
             * of saved music is for - the queue is the whole point of having
             * saved them together.
             */
            void play(track, items, 'profile');
            onPicked();
          }}
          className="flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2 py-2 text-left transition-colors hover:bg-slate"
        >
          <Artwork track={track} />

          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink">
              {track.title}
            </span>
            <span className="block truncate text-[11px] text-faint">
              {track.owner?.display_name ?? track.owner?.username ?? ''}
            </span>
          </span>
        </button>
      ))}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Who is on air                                                              */
/* -------------------------------------------------------------------------- */

function HostsTab({ onPicked }: { onPicked: () => void }) {
  const navigate = useNavigate();

  /*
   * The shared hook, not a query of its own: the header count, the dashboard
   * rail and this are the same question, and TanStack folds them onto one
   * request. Two counters for the same thing disagreeing is how people stop
   * trusting either.
   */
  const { items, isPending } = useLiveHosts();

  if (isPending) return <Loading />;

  if (items.length === 0) {
    return <Empty>Nobody is on air right now.</Empty>;
  }

  return (
    <>
      {items.map((room) => (
        <button
          key={room.id}
          type="button"
          data-library-host={room.username}
          onClick={() => {
            navigate(`/${room.username}`);
            onPicked();
          }}
          className="flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2 py-2 text-left transition-colors hover:bg-slate"
        >
          {/* `LiveRoom` carries no avatar, so this is initials by design. */}
          <Avatar src={null} name={room.host?.display_name ?? room.username} size={32} />

          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink">
              {room.host?.display_name ?? room.username}
            </span>
            <span className="block truncate text-[11px] text-faint">
              {room.now_playing?.title ?? room.name}
            </span>
          </span>

          <span className="meta-caps shrink-0 text-accent">
            {room.listener_count > 0 ? `${room.listener_count} on` : 'Live'}
          </span>
        </button>
      ))}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Playlists                                                                  */
/* -------------------------------------------------------------------------- */

function PlaylistsTab({
  username,
  onOpen,
}: {
  username: string;
  onOpen: (id: number) => void;
}) {
  const client = useQueryClient();
  const [naming, setNaming] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['playlists', username],
    queryFn: () => playlists.mine(username),
  });

  const items = data?.items ?? [];

  return (
    <>
      {naming ? (
        <NewPlaylist
          onDone={(made) => {
            setNaming(false);

            if (made === null) return;

            void client.invalidateQueries({ queryKey: ['playlists', username] });
            onOpen(made.id);
          }}
        />
      ) : (
        <button
          type="button"
          data-library-new-playlist
          onClick={() => setNaming(true)}
          className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2 py-2 text-left text-[13px] font-medium text-accent transition-colors hover:bg-slate"
        >
          <span className="font-mono text-sm">+</span>
          New playlist
        </button>
      )}

      {isPending && <Loading />}

      {!isPending && items.length === 0 && (
        <Empty>No playlists yet. Make one, then add whatever is playing to it.</Empty>
      )}

      {items.map((list) => (
        <button
          key={list.id}
          type="button"
          data-library-playlist={list.id}
          onClick={() => onOpen(list.id)}
          className="flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2 py-2 text-left transition-colors hover:bg-slate"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink">
              {list.title}
            </span>
            <span className="block truncate text-[11px] text-faint">
              {list.track_count} {list.track_count === 1 ? 'track' : 'tracks'}
              {list.visibility !== 'public' && ` · ${list.visibility}`}
            </span>
          </span>

          <span aria-hidden className="shrink-0 font-mono text-[11px] text-ghost">
            ›
          </span>
        </button>
      ))}
    </>
  );
}

/**
 * Creating one, inline.
 *
 * A dialog for a single text field, over a panel that is itself a popover, is
 * two layers of chrome for one word.
 */
export function NewPlaylist({ onDone }: { onDone: (made: Playlist | null) => void }) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const create = async () => {
    const name = title.trim();

    if (name === '' || busy) return;

    setBusy(true);
    setFailed(false);

    try {
      // The whole thing, not just an id: callers want to say its name back to
      // the person who just typed it.
      onDone(await playlists.create({ title: name }));
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-1.5 px-1 py-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        void create();
      }}
    >
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              onDone(null);
            }
          }}
          placeholder="Playlist name"
          aria-label="Playlist name"
          data-playlist-name
          maxLength={120}
          className="min-w-0 flex-1 rounded-[var(--radius-pill)] border border-line bg-app px-3 py-1.5 text-[13px] text-ink outline-none transition-colors placeholder:text-faint focus:border-line-strong"
        />

        <button
          type="submit"
          disabled={title.trim() === '' || busy}
          data-playlist-create
          className="meta-caps shrink-0 rounded-[var(--radius-pill)] bg-accent px-3 py-1.5 text-accent-ink transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {busy ? '…' : 'Make'}
        </button>
      </div>

      {failed && (
        <p className="px-1 text-[11px] text-danger">
          That did not save. Try again.
        </p>
      )}

      <p className="px-1 text-[10px] text-ghost">Private until you say otherwise.</p>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared scraps                                                              */
/* -------------------------------------------------------------------------- */

function Loading() {
  return <p className="px-2 py-3 font-mono text-[10px] text-ghost">loading…</p>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-2 py-3 text-xs text-faint">{children}</p>;
}

export function Artwork({ track }: { track: TrackSummary }) {
  const src = track.artwork_small ?? track.artwork ?? null;

  return src ? (
    <img src={src} alt="" className="h-8 w-8 shrink-0 rounded-[var(--radius-control)] object-cover" />
  ) : (
    <div
      aria-hidden
      className="h-8 w-8 shrink-0 rounded-[var(--radius-control)]"
      style={{
        backgroundImage: 'repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)',
      }}
    />
  );
}
